"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { m, useReducedMotion } from "framer-motion";
import useAutoplayVisibility from "@/hooks/useAutoplayVisibility";

const BASE_W = 1240;
const BASE_H = 500;

const SLOTS = [
  { x: -0.33, y: 0.05, w: 0.312, h: 0.811 },
  { x: 0.0, y: 0.05, w: 0.42, h: 0.94 },
  { x: 0.33, y: 0.05, w: 0.312, h: 0.811 },
];

const spring = {
  type: "spring",
  stiffness: 80,
  damping: 20,
  restDelta: 0.001,
  restSpeed: 0.001,
  // Use GPU acceleration
  transformTemplate: ({ x, y, scale }) =>
    `translateX(${x}px) translateY(${y}px) scale(${scale})`,
};

function useResponsiveSlots() {
  const [slots, setSlots] = useState(SLOTS);

  useEffect(() => {
    function handleResize() {
      setSlots(SLOTS);
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return slots;
}

export default function BookingCards({ order, isDark }) {
  const [items, setItems] = useState(order);
  const [size, setSize] = useState({ w: BASE_W, h: BASE_H });
  const ref = useRef(null);
  const timerRef = useRef(null);
  const prefersReduced = useReducedMotion();

  const slots = useResponsiveSlots();

  useEffect(() => setItems(order), [order]);

  useEffect(() => {
    let resizeObserver;
    if (ref.current) {
      resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          const w = entry.contentRect.width;
          const h = (BASE_H / BASE_W) * w;
          requestAnimationFrame(() => {
            setSize({ w, h });
          });
        }
      });
      resizeObserver.observe(ref.current);
    }
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const start = () => {
    clear();
    if (prefersReduced) return;
    timerRef.current = setInterval(() => {
      setItems((prev) =>
        prev.length ? [prev[prev.length - 1], ...prev.slice(0, -1)] : prev
      );
    }, 2800);
  };
  const clear = () => timerRef.current && clearInterval(timerRef.current);

  useEffect(() => {
    start();
    return clear;
  }, []);

  useAutoplayVisibility({ ref, start, clear, threshold: 0.2 });

  return (
    <div
      ref={ref}
      className="relative w-full max-w-[1700px] aspect-[1240/500] mx-3 mobile:mx-4 sm:mx-5 md:mx-6 lg:mx-7 xl:mx-9 laptop:mx-15 2xl:mx-15 3xl:mx-20 4xl:mx-24 select-none touch-pan-y"
      onFocus={clear}
      onBlur={start}
    >
      {items.map((card, index) => {
        const def = slots[index];
        if (!def) return null;

        const slot = {
          x: def.x * size.w,
          y: def.y * size.h,
          w: def.w * size.w,
          h: def.h * size.h,
        };

        const baseW = slots[1].w * size.w;
        const baseH = slots[1].h * size.h;
        const scaleX = slot.w / baseW;
        const scaleY = slot.h / baseH;

        return (
          <m.div
            key={card.id}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            animate={{ x: slot.x, y: slot.y }}
            transition={spring}
            style={{
              zIndex: index === 1 ? 10 : 5,
              willChange: "transform",
              transform: "translateZ(0)",
            }}
          >
            <m.div
              className="relative overflow-hidden rounded-[16px] transform-gpu"
              style={{
                width: baseW,
                height: baseH,
                willChange: "transform, opacity",
              }}
              animate={{ width: slot.w, height: slot.h }}
              transition={spring}
            >
              <Image
                src={isDark ? card.darkBg : card.lightBg}
                alt={card.alt || card.id || "feature"}
                fill
                sizes={
                  index === 1
                    ? "(max-width: 480px) 95vw, (max-width: 768px) 80vw, (max-width: 1200px) 50vw, 560px"
                    : "(max-width: 480px) 45vw, (max-width: 768px) 30vw, (max-width: 1200px) 22vw, 320px"
                }
                quality={index === 1 ? 90 : 68}
                priority={index === 1}
                loading={index === 1 ? "eager" : "lazy"}
                decoding="async"
                placeholder={
                  typeof (isDark ? card.darkBg : card.lightBg) === "string"
                    ? undefined
                    : "blur"
                }
                className="object-cover will-change-auto"
              />

              {index !== 1 && (
                <div
                  className="absolute inset-0 bg-overlay pointer-events-none"
                  style={{ willChange: "opacity", transform: "translateZ(0)" }}
                />
              )}

              <div
                className={`
    absolute left-[5%] sm:left-[22px]
    ${
      index === 1
        ? "top-[68%] mobile:top-[72%] sm:top-[75%] md:top-[78%] lg:top-[80%] xl:top-[82%] 2xl:top-[84%]"
        : "top-[60%] mobile:top-[62%] sm:top-[65%] md:top-[68%] lg:top-[70%] xl:top-[72%] 2xl:top-[74%]"
    }
  `}
              >
                <p
                  className="font-manrope dark:text-[#FFFFFF] text-[#0A000C] 
      w-[160px] sm:w-[220px] md:w-[240px] lg:w-[260px] xl:w-[273px] 2xl:w-[300px]
      leading-[10px] mobile:leading-[14px] sm:leading-[22px] md:leading-[24px] lg:leading-[26px] xl:leading-[28px] 2xl:leading-[30px]
      tracking-[0.02em]"
                >
                  <span
                    className="font-medium text-[8px] mobile:text-[10px] sm:text-[12px] md:text-[14px] tablet:text-[16px] lg:text-[18px] xl:text-[19px] 3xl:text-[20px] 
        opacity-90"
                  >
                    {card.line1}
                  </span>
                  <br />
                  <span className="font-bold text-[10px] mobile:text-[12px] sm:text-[14px] md:text-[16px] tablet:text-[20px] lg:text-[22px] xl:text-[24px] 2xl:text-[26px]">
                    {card.line2}
                  </span>
                </p>
              </div>
            </m.div>
          </m.div>
        );
      })}
    </div>
  );
}
